import { useMemo, useState } from 'react';
import clsx from 'clsx';
import styles from './GstSummaryTable.module.scss';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { GstSummaryView } from '../../../../../store/dashboard/revenueVendorExpenses/types';
import { GST_RANGE_KEYS, GstRangeKey } from '../../GstOverview/useGstDateRange';
import { DataAvailableCell, SummaryAmountCell } from './GstSummaryCells';
import { rowHasReturn } from './gstSummaryAdapter';
import { useGstSummaryData } from './useGstSummaryData';
import {
    DATA_AVAILABLE_ROW_LABEL,
    GST_SUMMARY_NO_DATA_LABEL,
    GST_SUMMARY_PROCESSING_LABEL,
    GST_SUMMARY_LOADING_LABEL,
    GST_SUMMARY_ERROR_LABEL,
    GST_RETURN_LABELS,
    GST_RETURN_OPTIONS_BY_VIEW,
    GST_SUMMARY_TITLE,
    SUMMARY_TOTAL_AMOUNTS_KEY,
} from './constants';

interface GstSummaryTableProps {
    view: GstSummaryView;
    fetchHeadlinePanelData?: (isPolling?: boolean) => void;
    rangeKey?: GstRangeKey;
    fetchStartDate?: string;
    fetchEndDate?: string;
    unitDivisor?: number;
}

// 40px high, border included (the shared selector's default is 42px); options fill the height and centre their label.
const TOGGLE_STYLE = { boxSizing: 'border-box', height: '2.5rem' } as const;
const TOGGLE_OPTION_STYLE = { fontFamily: 'var(--text-gilroy-medium)', alignSelf: 'stretch', padding: '0 1rem' } as const;

// One table per tab showing a single return: one column per month plus the Total. Rows the selected
// return does not carry are hidden. The plain table keeps the sticky label column and badge row.
// The GSTR toggle lives here so the selection survives unit / Time Period changes; each change refetches
// the summary for that return (the selection is the `returnType` of the request).
function GstSummaryTable({
    view,
    fetchHeadlinePanelData,
    rangeKey = GST_RANGE_KEYS.ALL,
    fetchStartDate = '',
    fetchEndDate = '',
    unitDivisor = 1,
}: GstSummaryTableProps) {
    const returnOptions = GST_RETURN_OPTIONS_BY_VIEW[view];
    const [selectedReturn, setSelectedReturn] = useState(returnOptions[0]);
    const { loading, hasError, isProcessing, model, gstConnectionStatus } = useGstSummaryData({
        view,
        returnType: selectedReturn,
        rangeKey,
        fetchStartDate,
        fetchEndDate,
        fetchHeadlinePanelData,
    });
    const { months, hasTotal, availability } = model;
    const rows = useMemo(() => model.rows.filter((row) => rowHasReturn(row, selectedReturn)), [model.rows, selectedReturn]);
    const hasColumns = months.length > 0;
    const footnotes = useMemo(
        () =>
            Array.from(new Set(rows.flatMap((row) => [row.notes[selectedReturn]].filter(Boolean)))).map((text, i) => ({
                text,
                id: `gst-summary-${view}-footnote-${i}`,
            })),
        [rows, view, selectedReturn],
    );
    const footnoteIdByText = useMemo(() => new Map(footnotes.map(({ text, id }) => [text, id])), [footnotes]);

    // A user-initiated load shows its spinner in the middle of the table area; the header keeps only the status.
    // A load error wins over the processing status, so a failed fetch is never hidden behind "Processing".
    const headerRight = loading ? (
        ''
    ) : hasError ? (
        <span className={styles.NoData}>{GST_SUMMARY_ERROR_LABEL}</span>
    ) : isProcessing ? (
        <div className={styles.Status}>
            <LoadingSpinner color={'var(--primary-purple)'} height={'1.25rem'} />
            <span className={styles.NoData}>{GST_SUMMARY_PROCESSING_LABEL}</span>
        </div>
    ) : hasColumns ? (
        ''
    ) : (
        <span className={styles.NoData}>{GST_SUMMARY_NO_DATA_LABEL}</span>
    );

    return (
        <div className={styles.GstSummary} data-testid={`gst-summary-${view}`}>
            <HeaderBodyBox
                title={
                    <div className={styles.TitleRow}>
                        <span className={styles.Title}>{GST_SUMMARY_TITLE}</span>
                        <CategorySelector
                            categoryOptions={returnOptions.map((code) => ({ id: code, label: GST_RETURN_LABELS[code] }))}
                            selectedCategory={selectedReturn}
                            changeSelectedTab={setSelectedReturn}
                            style={TOGGLE_STYLE}
                            categoryStyle={TOGGLE_OPTION_STYLE}
                            equalWidth
                        />
                    </div>
                }
                headerRight={headerRight}
                headerClassName={styles.Header}
                className={clsx({ [styles.TopNoData]: !loading && !hasColumns })}
            >
                {loading && (
                    <div className={styles.LoadingWrap} role="status" aria-label={GST_SUMMARY_LOADING_LABEL}>
                        <LoadingSpinner color={'var(--primary-purple)'} height={'2rem'} />
                    </div>
                )}
                {!loading && hasColumns && (
                    <div className={styles.TableContainer}>
                        <table className={styles.Table}>
                            <thead>
                                <tr>
                                    <th scope="col" className={clsx(styles.HeaderCell, styles.Classification)}>
                                        {model.classificationLabel}
                                    </th>
                                    {months.map((month) => (
                                        <th scope="col" key={month.key} className={styles.HeaderCell}>
                                            {month.label}
                                        </th>
                                    ))}
                                    {hasTotal && (
                                        <th scope="col" className={clsx(styles.HeaderCell, styles.TotalColumn)}>
                                            Total
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th scope="row" className={clsx(styles.Cell, styles.Classification)}>
                                        {DATA_AVAILABLE_ROW_LABEL}
                                    </th>
                                    {months.map((month) => (
                                        <DataAvailableCell
                                            key={month.key}
                                            monthKey={month.key}
                                            returnCode={selectedReturn}
                                            availability={availability}
                                            gstConnectionStatus={gstConnectionStatus}
                                        />
                                    ))}
                                    {hasTotal && <td className={clsx(styles.Cell, styles.TotalColumn)}>-</td>}
                                </tr>
                                {rows.map((row) => (
                                    <tr key={row.key} className={clsx({ [styles.CreditNoteRow]: row.isCreditNote })}>
                                        <th
                                            scope="row"
                                            className={clsx(styles.Cell, styles.Classification, {
                                                [styles.Bold]: row.isBold,
                                                [styles.CreditNote]: row.isCreditNote,
                                            })}
                                        >
                                            {row.label}
                                        </th>
                                        {months.map((month) => (
                                            <SummaryAmountCell
                                                key={month.key}
                                                row={row}
                                                periodKey={month.key}
                                                returnCode={selectedReturn}
                                                unitDivisor={unitDivisor}
                                                footnoteId={footnoteIdByText.get(row.notes[selectedReturn])}
                                            />
                                        ))}
                                        {hasTotal && (
                                            <SummaryAmountCell
                                                row={row}
                                                periodKey={SUMMARY_TOTAL_AMOUNTS_KEY}
                                                returnCode={selectedReturn}
                                                unitDivisor={unitDivisor}
                                                isTotalColumn
                                                footnoteId={footnoteIdByText.get(row.notes[selectedReturn])}
                                            />
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {footnotes.map(({ text, id }) => (
                            <div key={id} id={id} className={styles.Footnote} data-testid="gst-summary-footnote">
                                * {text}
                            </div>
                        ))}
                    </div>
                )}
            </HeaderBodyBox>
        </div>
    );
}

export default GstSummaryTable;
